import React, { createContext, useContext, useState, useEffect, useRef, ReactNode, forwardRef, useImperativeHandle } from "react";
import DashboardService from "../services/dashboard.service";
import {
  DashboardFiltersDto,
  DashboardSummary,
  BillingVsRevenue,
  ProjectStatusCount,
  UpcomingEventsData,
  EventControlOptions,
  EventControlRankings,
  EventControlMmsByMonth,
  EventControlMonthlyCards,
} from "../types/dashboard";
import { AuthContext } from "../contexts/auth";

interface DashboardContextData {
  // Valores iniciais (sem filtros)
  initialSummary: DashboardSummary | null;
  initialBillingVsRevenue: BillingVsRevenue | null;
  initialProjectStatusCount: ProjectStatusCount[] | null;

  // Valores filtrados
  filteredSummary: DashboardSummary | null;
  filteredBillingVsRevenue: BillingVsRevenue | null;
  filteredProjectStatusCount: ProjectStatusCount[] | null;
  
  // Dados específicos para o CEO Dashboard
  upcomingEvents: UpcomingEventsData | null;

  // Estado de carregamento
  loading: boolean;

  // Função para aplicar filtros
  applyFilters: (filters: DashboardFiltersDto) => Promise<void>;

  // Filtros atuais
  currentFilters: DashboardFiltersDto;

  // Tipo de dashboard atualmente selecionado
  selectedDashboardType?: string;

  // Funções para filtrar por ID
  toggleCellFilter: (cellId: string) => void;
  toggleCollaboratorFilter: (collaboratorId: string) => void;
  toggleClientFilter: (clientId: string) => void;

  // Função para verificar se há filtros aplicados
  hasFilters: boolean;

  // Total de projetos filtrados (vindo de /dashboard/projects)
  filteredProjectsTotal: number | null;
  setFilteredProjectsTotal: (total: number | null) => void;

  // Opções de filtro do event-control (clientes, células, status)
  eventControlOptions: EventControlOptions | null;

  // Rankings do event-control (células, colaboradores, clientes)
  initialEventControlRankings: EventControlRankings | null;
  filteredEventControlRankings: EventControlRankings | null;


  // Gráfico de MMs por mês (event-control) - barras (geral) e linhas (filtrado)
  mmsByMonthGeneral: EventControlMmsByMonth | null;
  mmsByMonthFiltered: EventControlMmsByMonth | null;

  // Cards mensais de MMs e auditados (event-control)
  eventControlMonthlyCards: EventControlMonthlyCards | null;

  // ID da sessão para debug logs
  debugSessionId: string;
}

const DashboardContext = createContext<DashboardContextData>({} as DashboardContextData);

export const useDashboard = () => useContext(DashboardContext);

interface DashboardProviderProps {
  children: ReactNode;
  selectedDashboardType?: string;
  ref?: React.Ref<DashboardContextRefType>;
}

// Tipo para as funções expostas através da ref
export interface DashboardContextRefType {
  applyFilters: (filters: DashboardFiltersDto) => Promise<void>;
}

export const DashboardProvider: React.FC<DashboardProviderProps> = forwardRef(({
  children,
  selectedDashboardType,
}, ref) => {
  const { user: currentUser } = useContext(AuthContext);
  
  // Estados para valores iniciais
  const [initialSummary, setInitialSummary] = useState<DashboardSummary | null>(null);
  const [initialBillingVsRevenue, setInitialBillingVsRevenue] = useState<BillingVsRevenue | null>(
    null
  );
  const [initialProjectStatusCount, setInitialProjectStatusCount] = useState<
    ProjectStatusCount[] | null
  >(null);

  // Estados para valores filtrados
  const [filteredSummary, setFilteredSummary] = useState<DashboardSummary | null>(null);
  const [filteredBillingVsRevenue, setFilteredBillingVsRevenue] = useState<BillingVsRevenue | null>(
    null
  );
  const [filteredProjectStatusCount, setFilteredProjectStatusCount] = useState<
    ProjectStatusCount[] | null
  >(null);

  // Estado de carregamento
  const [loading, setLoading] = useState(true);

  // Filtros atuais
  const [currentFilters, setCurrentFilters] = useState<DashboardFiltersDto>({});

  // AbortController ref para cancelar requisições pendentes ao trocar filtros
  const abortControllerRef = useRef<AbortController | null>(null);

  // Session ID para rastreamento de debug (gerado uma vez no mount)
  const [debugSessionId] = useState<string>(() =>
    `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
  );

  // Estado para armazenar os dados de próximos eventos (CEO dashboard)
  const [upcomingEvents, setUpcomingEvents] = useState<UpcomingEventsData | null>(null);

  // Total de projetos filtrados alimentado pelo EventsList
  const [filteredProjectsTotal, setFilteredProjectsTotal] = useState<number | null>(null);

  // Opções de filtro do event-control
  const [eventControlOptions, setEventControlOptions] = useState<EventControlOptions | null>(null);

  // Rankings do event-control
  const [initialEventControlRankings, setInitialEventControlRankings] = useState<EventControlRankings | null>(null);
  const [filteredEventControlRankings, setFilteredEventControlRankings] = useState<EventControlRankings | null>(null);


  // Gráfico de MMs por mês (event-control) - barras (geral) e linhas (filtrado)
  const [mmsByMonthGeneral, setMmsByMonthGeneral] = useState<EventControlMmsByMonth | null>(null);
  const [mmsByMonthFiltered, setMmsByMonthFiltered] = useState<EventControlMmsByMonth | null>(null);

  // Cards mensais de MMs e auditados (event-control)
  const [eventControlMonthlyCards, setEventControlMonthlyCards] = useState<EventControlMonthlyCards | null>(null);

  // Atualizar o dashboard quando o tipo selecionado muda
  useEffect(() => {
    // Apenas reagimos quando há um tipo de dashboard definido
    if (selectedDashboardType) {
      console.log(`Dashboard type changed to: ${selectedDashboardType}`);
    }
  }, [selectedDashboardType]);

  // Cleanup do AbortController ao desmontar o provider
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Função para aplicar filtros
  const applyFilters = async (filters: DashboardFiltersDto) => {
    // Cancela requisições pendentes da interação anterior
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const { signal } = controller;

    try {
      setLoading(true);
      setCurrentFilters(filters);
      setFilteredProjectsTotal(null);

      // Normaliza datas para ISO 8601 UTC midnight (YYYY-MM-DDTHH:mm:ss.sssZ)
      const normalizeDateToISO = (value: any, endOfDay = false): string | undefined => {
        if (!value) return undefined;

        if (typeof value === "string") {
          const date = new Date(value);
          if (Number.isNaN(date.getTime())) return undefined;
          return new Date(Date.UTC(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            endOfDay ? 23 : 0,
            endOfDay ? 59 : 0,
            endOfDay ? 59 : 0,
            endOfDay ? 999 : 0
          )).toISOString();
        }

        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return undefined;

        return new Date(Date.UTC(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
          endOfDay ? 23 : 0,
          endOfDay ? 59 : 0,
          endOfDay ? 59 : 0,
          endOfDay ? 999 : 0
        )).toISOString();
      };

      // Normaliza cada par startDate/endDate, aplicando fallback de 30 dias se incompleto
      const normalizeDateRange = (
        range: { start?: any; end?: any } | undefined
      ): { start?: string; end?: string } | undefined => {
        if (!range) return undefined;
        const start = normalizeDateToISO(range.start, false);
        const end = normalizeDateToISO(range.end, true);

        if (start && !end) {
          const endDate = new Date(start);
          endDate.setUTCDate(endDate.getUTCDate() + 30);
          endDate.setUTCHours(23, 59, 59, 999);
          return { start, end: endDate.toISOString() };
        }
        if (!start && end) {
          const startDate = new Date(end);
          startDate.setUTCDate(startDate.getUTCDate() - 30);
          startDate.setUTCHours(0, 0, 0, 0);
          return { start: startDate.toISOString(), end };
        }
        if (start && end) {
          return { start, end };
        }
        return undefined;
      };

      // Formata as datas para a chamada da API com ISO 8601 UTC
      const apiFilters = {
        ...filters,
        debugSessionId,
        startDate: filters.startDate
          ? normalizeDateRange(filters.startDate)
          : undefined,
        endDate: filters.endDate
          ? normalizeDateRange(filters.endDate)
          : undefined,
      };

      // DEBUG: datas antes e depois da normalização
      console.log('[DashboardContext] Date normalization:', {
        rawStartDate: filters.startDate,
        rawEndDate: filters.endDate,
        normalizedStartDate: apiFilters.startDate,
        normalizedEndDate: apiFilters.endDate,
      });
      
      // Se o dashboard é CEO, remover periodType e periodValue dos filtros
      if (selectedDashboardType === "CEO") {
        delete apiFilters.periodType;
        delete apiFilters.periodValue;
      }

      // Determinar se é o carregamento inicial (sem filtros de dimensão)
      const isInitialLoad = !filters.cellId && !filters.clientId && !filters.collaboratorId
        && !filters.status?.length && !filters.startDate && !filters.endDate;

      // DEBUG: registrar filtros enviados às APIs
      console.log('[DashboardContext] applyFilters:', {
        dashboardType: selectedDashboardType,
        startDate: apiFilters.startDate,
        endDate: apiFilters.endDate,
        status: apiFilters.status,
        cellId: apiFilters.cellId,
        clientId: apiFilters.clientId,
        collaboratorId: apiFilters.collaboratorId,
        isInitialLoad,
      });

      // Array para armazenar as promessas das chamadas de API
      const promises: Promise<any>[] = [];
      const promiseTypes: string[] = [];

      // Determinar quais APIs chamar com base no tipo de dashboard
      if (selectedDashboardType === "CEO") {
        // Para o CEO, chamamos apenas summary e projects
        promises.push(DashboardService.getSummary(apiFilters, signal));
        promises.push(DashboardService.getEventControlUpcomingEvents(apiFilters, signal));
        promises.push(DashboardService.getEventControlSummary(apiFilters, signal));
        promiseTypes.push("summary", "upcomingEvents", "eventControlSummary");
      } else if (selectedDashboardType === "Supervisor" || selectedDashboardType === "Gerente") {
        // Para Supervisor e Gerente, chamamos summary e as APIs de métricas
        promises.push(DashboardService.getSummary(apiFilters, signal));
        promises.push(DashboardService.getEventControlSummary(apiFilters, signal));
        promises.push(DashboardService.getEventControlRankings(apiFilters, signal));
        // Gráfico MMs por mês: barras sempre do geral (sem filtro) + linhas do filtrado
        promises.push(DashboardService.getEventControlMmsByMonth({}, signal));
        promises.push(DashboardService.getEventControlMmsByMonth(apiFilters, signal));
        promises.push(DashboardService.getEventControlMonthlyCards(apiFilters, signal));
        promises.push(DashboardService.getEventControlOptions(apiFilters, signal));
        promiseTypes.push(
          "summary",
          "eventControlSummary",
          "eventControlRankings",
          "mmsByMonthGeneral",
          "mmsByMonthFiltered",
          "eventControlMonthlyCards",
          "eventControlOptions"
        );
        if (isInitialLoad) {
          promises.push(DashboardService.getBillingVsRevenue(apiFilters, signal));
          promises.push(DashboardService.getProjectStatusCount(apiFilters, signal));
          promiseTypes.push("billingVsRevenue", "projectStatusCount");
        }
      } else {
        // Caso inicial (dashboard não definido) ou fallback
        promises.push(DashboardService.getSummary(apiFilters, signal));
        promises.push(DashboardService.getEventControlSummary(apiFilters, signal));
        promises.push(DashboardService.getEventControlRankings(apiFilters, signal));
        promises.push(DashboardService.getEventControlMmsByMonth({}, signal));
        promises.push(DashboardService.getEventControlMmsByMonth(apiFilters, signal));
        promises.push(DashboardService.getEventControlOptions(apiFilters, signal));
        promiseTypes.push(
          "summary",
          "eventControlSummary",
          "eventControlRankings",
          "mmsByMonthGeneral",
          "mmsByMonthFiltered",
          "eventControlOptions"
        );
      }

      // Executar as chamadas selecionadas — falhas individuais não bloqueiam o restante
      const settledResults = await Promise.allSettled(promises);

      // Se o controller foi abortado após as requisições, descarta resultados
      if (signal.aborted) return;

      // Atualizar os estados de acordo com os resultados
      settledResults.forEach((settled, index) => {
        const type = promiseTypes[index];
        if (settled.status === 'rejected') {
          if (settled.reason?.name === 'CanceledError' || settled.reason?.code === 'ERR_CANCELED') return;
          console.warn(`[Dashboard] falha em "${type}":`, settled.reason);
          return;
        }
        const result = settled.value;
        
        switch (type) {
          case "summary": {
            const summaryWithPhases = isInitialLoad
              ? { ...result }
              : result;
            setFilteredSummary(summaryWithPhases);
            if (isInitialLoad) setInitialSummary(summaryWithPhases);
            break;
          }
          case "upcomingEvents":
            setUpcomingEvents(result);
            break;
          case "eventControlSummary": {
            const ecMerge = (prev: any) =>
              prev
                ? {
                    ...prev,
                    expectedBilling: result?.expectedBilling ?? prev.expectedBilling,
                    expectedRevenue: result?.expectedRevenue ?? prev.expectedRevenue,
                    totalAudited: result?.totalAudited ?? prev.totalAudited,
                    // Usar o valor diretamente — nunca cair no prev, pois 0 é válido
                    totalMMs: result?.totalMMs !== undefined ? result.totalMMs : prev.totalMMs,
                    phases: result?.phases,
                  }
                : prev;
            setFilteredSummary(ecMerge);
            if (isInitialLoad) setInitialSummary(ecMerge);
            break;
          }
          case "eventControlRankings":
            setFilteredEventControlRankings(result);
            if (isInitialLoad) setInitialEventControlRankings(result);
            break;
          case "mmsByMonthGeneral":
            setMmsByMonthGeneral(result);
            break;
          case "mmsByMonthFiltered":
            setMmsByMonthFiltered(result);
            break;
          // Removido: eventControlMmsByMonth legado
          case "eventControlMonthlyCards":
            setEventControlMonthlyCards(result);
            break;
          case "eventControlOptions":
            setEventControlOptions(result);
            break;
          case "billingVsRevenue":
            setInitialBillingVsRevenue(result);
            setFilteredBillingVsRevenue(result);
            break;
          case "projectStatusCount":
            setInitialProjectStatusCount(result);
            setFilteredProjectStatusCount(result);
            break;
        }
      });
      
      // Não atualizamos o projectStatusCount e billingVsRevenue, pois eles só devem ser carregados no início
    } catch (error: any) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
      console.error("Erro ao aplicar filtros no dashboard:", error);
    } finally {
      if (!signal.aborted) {
        setLoading(false);
      }
    }
  };

  const toggleCellFilter = (cellId: string) => {
    setCurrentFilters((prev) => {
      const isAlreadyFiltered = prev.cellId === cellId;
      let newFilters;

      if (isAlreadyFiltered) {
        // Se já está filtrado, remove o filtro
        newFilters = { ...prev };
        delete newFilters.cellId;
      } else {
        // Se não está filtrado, adiciona o filtro
        newFilters = {
          ...prev,
          cellId,
        };
      }

      // Aplica os novos filtros
      applyFilters(newFilters);
      return newFilters;
    });
  };

  const toggleCollaboratorFilter = (collaboratorId: string) => {
    setCurrentFilters((prev) => {
      const isAlreadyFiltered = prev.collaboratorId === collaboratorId;
      let newFilters;

      if (isAlreadyFiltered) {
        // Se já está filtrado, remove o filtro
        newFilters = { ...prev };
        delete newFilters.collaboratorId;
      } else {
        // Se não está filtrado, adiciona o filtro
        newFilters = {
          ...prev,
          collaboratorId,
        };
      }

      // Aplica os novos filtros
      applyFilters(newFilters);
      return newFilters;
    });
  };

  const toggleClientFilter = (clientId: string) => {
    setCurrentFilters((prev) => {
      const isAlreadyFiltered = prev.clientId === clientId;
      let newFilters;

      if (isAlreadyFiltered) {
        // Se já está filtrado, remove o filtro
        newFilters = { ...prev };
        delete newFilters.clientId;
      } else {
        // Se não está filtrado, adiciona o filtro
        newFilters = {
          ...prev,
          clientId,
        };
      }

      // Aplica os novos filtros
      applyFilters(newFilters);
      return newFilters;
    });
  };

  // Verificar se há filtros aplicados
  const hasFilters: boolean = Boolean(
    Object.keys(currentFilters).length > 0 &&
      (currentFilters.status?.length ||
        currentFilters.cellId ||
        currentFilters.collaboratorId ||
        currentFilters.clientId ||
        currentFilters.mmCodes?.length ||
        currentFilters.startDate?.start ||
        currentFilters.startDate?.end ||
        currentFilters.endDate?.start ||
        currentFilters.endDate?.end)
  );

  // Expor a função applyFilters através da ref
  useImperativeHandle(ref, () => ({
    applyFilters,
  }));

  return (
    <DashboardContext.Provider
      value={{
        initialSummary,
        initialBillingVsRevenue,
        initialProjectStatusCount,
        filteredSummary,
        filteredBillingVsRevenue,
        filteredProjectStatusCount,
        upcomingEvents,
        loading,
        applyFilters,
        currentFilters,
        toggleCellFilter,
        toggleCollaboratorFilter,
        toggleClientFilter,
        hasFilters,
        selectedDashboardType,
        filteredProjectsTotal,
        setFilteredProjectsTotal,
        eventControlOptions,
        initialEventControlRankings,
        filteredEventControlRankings,
        // eventControlMmsByMonth, // legado removido
        mmsByMonthGeneral,
        mmsByMonthFiltered,
        eventControlMonthlyCards,
        debugSessionId,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
});
